import { getAnalytics, isSupported, logEvent, type Analytics } from 'firebase/analytics';
import { getFirebaseApp, isFirebaseConfigured } from './config';

let analytics: Analytics | null = null;
let initTried = false;

async function ensureAnalytics(): Promise<Analytics | null> {
  if (!isFirebaseConfigured) return null;
  if (analytics || initTried) return analytics;
  initTried = true;
  const app = getFirebaseApp();
  const supported = await isSupported().catch(() => false);
  if (app && supported) analytics = getAnalytics(app);
  return analytics;
}

// Fire-and-forget event logging; a no-op when Firebase/Analytics is unavailable.
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  ensureAnalytics()
    .then((a) => {
      if (a) logEvent(a, name, params);
    })
    .catch(() => {
      /* analytics failures must never break the app */
    });
}

// Typed helpers for the PRD event catalog. (Daily/Weekly Active Users and
// session duration are collected automatically by Firebase Analytics.)
export const analyticsEvents = {
  // Authentication
  signUp: (method = 'email') => trackEvent('sign_up', { method }),
  login: (method = 'email') => trackEvent('login', { method }),
  logout: () => trackEvent('logout'),
  // Learning
  subjectSelected: (subject: string | number) => trackEvent('subject_selected', { subject }),
  chapterStarted: (chapter: string | number) => trackEvent('chapter_started', { chapter }),
  chapterCompleted: (chapter: string | number) => trackEvent('chapter_completed', { chapter }),
  mockStarted: (mockId: string | number) => trackEvent('mock_started', { mockId }),
  mockSubmitted: (mockId: string | number, score?: number) =>
    trackEvent('mock_submitted', { mockId, score }),
  // Commerce
  subscriptionViewed: () => trackEvent('subscription_viewed'),
  checkoutStarted: () => trackEvent('checkout_started'),
  paymentSuccess: (plan?: string) => trackEvent('payment_success', { plan }),
  paymentFailed: (reason?: string) => trackEvent('payment_failed', { reason }),
};
